import { useState } from "react";
import { Btn, Card, Dropdown, Icon, Input, Pill, T } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { Screen, PageHeader } from "../../shell/chrome";
import { FilterBar, ExplainerFooter, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID } from "../../shell/list-composites";
import { eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { EventsSectionState } from "../events/EventsSectionState";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { TicketsSubTabs } from "./TicketsSubtabs";
import { TicketGroupStore, useTicketGroups } from "./ticket-groups-store";
import { TicketTypeStore, useTicketTypes, TT_KINDS, ttKindPill, ttMoney, ttTh, ttTd, type TicketType } from "./ticket-types-store";
import { useT, t as i18nT } from "../../lib/i18n";

export default function TicketTypesLibrary() {
  const t = useT();
  const translate = t;
  const nav = useNav();
  const types = useTicketTypes();
  const groups = useTicketGroups();
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [view, setView] = useViewMode("ticket-types:view");
  const canWrite = can("events.write");

  const filtered = types.filter((t) => {
    if (kindFilter && t.kind !== kindFilter) return false;
    if (query.trim() && !t.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const usedIn = (id: string) => TicketGroupStore.all().filter((g) => (g.tiers || []).some((ti) => ti.typeId === id)).length;

  const newType = () => nav.go("ticket-types.edit", { new: 1 });
  const dup = async (t: TicketType) => { try { const c = await TicketTypeStore.duplicate(t.id); if (c) toast({ tone: "success", title: translate("tickets.typesLibrary.ticketTypeDuplicated", "Ticket type duplicated"), subtitle: i18nT("tickets.typesLibrary.cNameAdded.icu", { value0: c.name }) }); } catch (e) { console.warn("duplicateTicketType failed:", e); toast({ tone: "danger", title: translate("tickets.typesLibrary.couldnTDuplicateTicketType", "Couldn't duplicate ticket type"), subtitle: translate("tickets.typesLibrary.retryInAMoment", "Retry in a moment.") }); } };
  const del = async (t: TicketType) => { try { await TicketTypeStore.remove(t.id); toast({ tone: "warn", title: translate("tickets.typesLibrary.ticketTypeDeleted", "Ticket type deleted"), subtitle: i18nT("tickets.typesLibrary.tNameRemovedFromTheLibrary.icu", { value0: t.name }) }); } catch (e) { console.warn("deleteTicketType failed:", e); toast({ tone: "danger", title: translate("tickets.typesLibrary.couldnTDeleteTicketType", "Couldn't delete ticket type"), subtitle: translate("tickets.typesLibrary.retryInAMoment", "Retry in a moment.") }); } };
  const actions = (t: TicketType) => ({
    onView: () => nav.go("ticket-types.edit", { id: t.id, view: 1 }),
    viewLabel: "View type",
    onEdit: canWrite ? () => nav.go("ticket-types.edit", { id: t.id }) : undefined,
    onDuplicate: canWrite ? () => dup(t) : undefined,
    audit: { type: "ticketType" as const, id: t.id, name: t.name },
    moreHeader: t.name,
    moreItems: canWrite ? [
      { icon: <Icon.layers size={14} />, label: translate("tickets.typesLibrary.useInATicketGroup", "Use in a ticket group"), onClick: () => nav.go("ticket-groups.edit", { new: 1 }) },
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: translate("tickets.typesLibrary.deleteType", "Delete type"), danger: true, onClick: () => del(t) },
    ] : [],
  });

  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav, "tickets", useEventSectionCounts())} activeTab="tickets">
      <TicketsSubTabs nav={nav} active="types" typesCount={types.length} groupsCount={groups.length} />
      <PageHeader
        title={t("tickets.typesLibrary.ticketTypes", "Ticket Types")}
        subtitle={t("tickets.typesLibrary.reusableTicketTypesPriceAccessAndCapacityTicketGroupsAreBuiltFrom", "Reusable ticket types — price, access and capacity. Ticket groups are built from these.")}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={newType}>{t("tickets.typesLibrary.newTicketType", "New ticket type")}</Btn> : null}
        </>}
      />
      {/* FilterBar carries its own 12px/24px padding — a wrapper adding 24px more
          pushed the search field out of alignment with the page title and the
          explainer below it (demo report, 2026-07-30). Only the explainer needs
          the page inset. */}
      <div style={{ paddingTop: 4 }}>
        <FilterBar>
          <Input placeholder={t("tickets.typesLibrary.findATicketType", "Find a ticket type…")} value={query} onChange={(e) => setQuery(e.target.value)} full={false} style={{ minWidth: 240 }} />
          <Dropdown
            options={[{ value: "all", label: t("tickets.typesLibrary.allKinds", "All kinds") }, ...TT_KINDS.map((k) => ({ value: k.id, label: k.label }))]}
            value={kindFilter || "all"}
            onChange={(v) => setKindFilter(v === "all" ? "" : v)}
            aria-label={t("tickets.typesLibrary.allKinds", "All kinds")}
            testid="tt-kind-filter"
            style={{ gap: 0, width: 180, flexShrink: 0 }}
          />
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: T.soft }}>{filtered.length} {t("tickets.typesLibrary.of", "of")}{" "}{types.length}</span>
        </FilterBar>

        <div style={{ padding: "0 24px" }}>
          <ExplainerFooter>
            <b>{t("tickets.typesLibrary.typesPowerGroups", "Types power groups.")}</b> {t("tickets.typesLibrary.defineATicketTypeOnceHereThenAddItToAnyTicket", "Define a ticket type once here, then add it to any ticket group. Editing a type updates its definition wherever it's referenced.")}</ExplainerFooter>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 28px", display: "flex", flexDirection: "column" }}>
        {filtered.length === 0 ? (
          <EventsSectionState noun={t("events.sectionState.nounTicketTypes", "ticket types")} icon={<Icon.ticket size={20} />}
            empty filtered={!!(query || kindFilter)} onClearFilters={() => { setQuery(""); setKindFilter(""); }}
            emptyBody={t("tickets.typesLibrary.createYourFirstReusableTicketType", "Create your first reusable ticket type.")}
            action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={newType}>{t("tickets.typesLibrary.newTicketType", "New ticket type")}</Btn> : null} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16, marginTop: 14 }}>
            {filtered.map((t) => {
              const pill = ttKindPill(t.kind);
              return (
                <Card key={t.id} style={{ padding: 0, display: "flex", flexDirection: "column", overflow: "hidden", cursor: "pointer" }}
                  onClick={() => nav.go("ticket-types.edit", canWrite ? { id: t.id } : { id: t.id, view: 1 })}>
                  <div style={{ height: 5, background: t.color }} />
                  <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{t.name || "Untitled type"}</div>
                        <div style={{ fontSize: 13, color: T.soft, marginTop: 3, lineHeight: 1.45 }}>{t.desc || "No description"}</div>
                      </div>
                      <Pill tone={pill.tone}>{pill.label}</Pill>
                    </div>
                    <div style={{ marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 16, fontSize: 12, color: T.soft }}>
                      {t.priceLocked ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: T.n1, fontWeight: 600 }}><Icon.lock size={12} /> {ttMoney(t.price)} {translate("tickets.typesLibrary.locked", "locked")}</span> : null}
                      <span style={{ marginLeft: "auto" }}>{usedIn(t.id)} {translate("tickets.typesLibrary.group", "group")}{usedIn(t.id) === 1 ? "" : translate('tickets.typesLibrary.text')}</span>
                    </div>
                  </div>
                  <CardActionBar {...actions(t)} />
                </Card>
              );
            })}
          </div>
        ) : (
          <Card style={{ padding: 0, marginTop: 14 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={ttTh}>{t("tickets.typesLibrary.ticketType", "Ticket type")}</th><th style={ttTh}>{t("tickets.typesLibrary.kind", "Kind")}</th><th style={ttTh}>{t("tickets.typesLibrary.pricing", "Pricing")}</th><th style={ttTh}>{t("tickets.typesLibrary.inGroups", "In groups")}</th><th style={{ ...ttTh, width: 140 }}>{t("tickets.typesLibrary.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, i) => {
                  const pill = ttKindPill(t.kind);
                  return (
                    <tr key={t.id} onClick={() => nav.go("ticket-types.edit", canWrite ? { id: t.id } : { id: t.id, view: 1 })}
                      style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", borderLeft: `3px solid ${t.color}` }}>
                      <td style={ttTd}>
                        <div style={{ color: T.n1, fontWeight: 600 }}>{t.name || "Untitled type"}</div>
                        <div style={{ color: T.soft, fontSize: 11.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 360 }}>{t.desc}</div>
                      </td>
                      <td style={ttTd}><Pill tone={pill.tone}>{pill.label}</Pill></td>
                      <td style={{ ...ttTd, color: T.n2 }}>{t.priceLocked ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: T.n1, fontWeight: 600 }}><Icon.lock size={12} /> {ttMoney(t.price)}</span> : <span style={{ color: T.soft }}>{translate("tickets.typesLibrary.setPerEvent", "Set per event")}</span>}</td>
                      <td style={{ ...ttTd, color: T.soft }}>{usedIn(t.id)}</td>
                      <td style={ttTd} onClick={(e) => e.stopPropagation()}><RowActions {...actions(t)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Screen>
  );
}
